import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type {
  FilamentSpool,
  FilamentSpoolInsert,
  FilamentSpoolUpdate,
} from '../lib/database.types'

const SPOOLS_KEY = ['filament_spools']

/**
 * Compute the manual adjustment that, combined with the existing usage
 * history, reproduces the user's submitted current_weight_grams. Pure
 * function so the invariant (reconcile + adj === user's reweigh) can be
 * pinned by tests. See spool-weight.test.ts.
 */
export function computeManualWeightAdjustment(args: {
  submittedCurrent: number
  effectiveOriginal: number
  totalUsedGrams: number
}): number {
  return args.submittedCurrent - (args.effectiveOriginal - args.totalUsedGrams)
}

export const FILAMENT_MATERIALS = [
  'PLA',
  'PLA+',
  'PLA Silk',
  'PLA Matte',
  'PLA GITD',
  'PLA Translucent',
  'PLA-CF',
  'PETG',
  'PETG-CF',
  'ABS',
  'ASA',
  'TPU',
  'Nylon',
  'PC',
  'Other',
] as const

export function useFilamentSpools() {
  return useQuery({
    queryKey: SPOOLS_KEY,
    queryFn: async (): Promise<FilamentSpool[]> => {
      const { data, error } = await supabase
        .from('filament_spools')
        .select('*')
        .order('status', { ascending: true })
        .order('created_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })
}

export function useCreateFilamentSpool() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (
      input: Omit<FilamentSpoolInsert, 'user_id'>,
    ): Promise<FilamentSpool> => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')
      // Partially-used spool back-fill: if current < original on a brand-new
      // spool, persist the delta so reconcile preserves it on the first print.
      const payload: typeof input & { manual_weight_adjustment_grams?: number } = {
        ...input,
      }
      if (
        payload.manual_weight_adjustment_grams === undefined &&
        payload.original_weight_grams !== undefined &&
        payload.current_weight_grams !== payload.original_weight_grams
      ) {
        payload.manual_weight_adjustment_grams =
          payload.current_weight_grams - payload.original_weight_grams
      }
      const { data, error } = await supabase
        .from('filament_spools')
        .insert({ ...payload, user_id: user.id })
        .select('*')
        .single()
      if (error) throw error

      // Cash-basis: spool purchase = expense at time of purchase.
      // Errors must surface — a silently-dropped expense breaks profit math.
      if (data.purchase_price != null) {
        const { error: expenseErr } = await supabase.from('expenses').insert({
          user_id: user.id,
          expense_date: data.purchase_date ?? new Date().toISOString().slice(0, 10),
          amount: Number(data.purchase_price),
          category: 'filament',
          linked_spool_id: data.id,
          description: `${data.brand ? data.brand + ' ' : ''}${data.color} ${data.material}`,
        })
        if (expenseErr) throw expenseErr
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SPOOLS_KEY })
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
    },
  })
}

export function useUpdateFilamentSpool() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: { id: string } & FilamentSpoolUpdate): Promise<FilamentSpool> => {
      // If the user is editing current_weight_grams directly (a kitchen-scale
      // reweigh), capture the delta as manual_weight_adjustment_grams so the
      // next reconcileSpoolWeight call doesn't overwrite their correction.
      // Skip if the caller already supplied an explicit adjustment.
      if (
        updates.current_weight_grams !== undefined &&
        updates.manual_weight_adjustment_grams === undefined
      ) {
        const { data: existing, error: readErr } = await supabase
          .from('filament_spools')
          .select('original_weight_grams')
          .eq('id', id)
          .single()
        if (readErr) throw readErr
        const effectiveOriginal =
          updates.original_weight_grams ?? existing.original_weight_grams
        const { data: usages, error: usageErr } = await supabase
          .from('print_filament_usage')
          .select('grams_used')
          .eq('spool_id', id)
        if (usageErr) throw usageErr
        const totalUsed = (usages ?? []).reduce((sum, u) => sum + u.grams_used, 0)
        updates.manual_weight_adjustment_grams = computeManualWeightAdjustment({
          submittedCurrent: updates.current_weight_grams,
          effectiveOriginal,
          totalUsedGrams: totalUsed,
        })
      }

      const { data, error } = await supabase
        .from('filament_spools')
        .update(updates)
        .eq('id', id)
        .select('*')
        .single()
      if (error) throw error

      // Sync the linked expense. Throw on any failure — a silent error
      // here makes filament spend disagree with cash-basis profit forever.
      const { data: existingExpense, error: expenseReadErr } = await supabase
        .from('expenses')
        .select('id')
        .eq('linked_spool_id', id)
        .maybeSingle()
      if (expenseReadErr) throw expenseReadErr

      if (data.purchase_price != null) {
        const expensePayload = {
          expense_date: data.purchase_date ?? new Date().toISOString().slice(0, 10),
          amount: Number(data.purchase_price),
          category: 'filament' as const,
          description: `${data.brand ? data.brand + ' ' : ''}${data.color} ${data.material}`,
        }
        if (existingExpense) {
          const { error: expUpdErr } = await supabase
            .from('expenses')
            .update(expensePayload)
            .eq('id', existingExpense.id)
          if (expUpdErr) throw expUpdErr
        } else {
          const { data: { user } } = await supabase.auth.getUser()
          if (user) {
            const { error: expInsErr } = await supabase.from('expenses').insert({
              ...expensePayload,
              user_id: user.id,
              linked_spool_id: data.id,
            })
            if (expInsErr) throw expInsErr
          }
        }
      } else if (existingExpense) {
        // purchase_price cleared — remove the expense
        const { error: expDelErr } = await supabase
          .from('expenses')
          .delete()
          .eq('id', existingExpense.id)
        if (expDelErr) throw expDelErr
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SPOOLS_KEY })
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
    },
  })
}

export function useDeleteFilamentSpool() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      // Delete linked expense first (CASCADE on expenses.linked_spool_id is set null, not delete,
      // so we need explicit cleanup). Throw on failure or we orphan the expense.
      const { error: expDelErr } = await supabase
        .from('expenses')
        .delete()
        .eq('linked_spool_id', id)
      if (expDelErr) throw expDelErr
      const { error } = await supabase.from('filament_spools').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SPOOLS_KEY })
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
    },
  })
}
